import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Sparkles, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

/**
 * Hero-level "Are you a merchant? Paste your site → we'll scan it." card.
 * Anonymous users are routed through signup with the URL preserved; signed-in
 * users (without a store) get auto-scrape on landing in /vendor-onboarding.
 */
const VendorQuickStart = () => {
  const nav = useNavigate();
  const { user } = useAuth();
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);

  // Hide the card entirely for users who already manage a store — they
  // already have a path forward via /vendor-admin.
  if (user?.vendor_id) return null;

  const go = () => {
    const trimmed = url.trim();
    if (!trimmed) {
      toast.error('Paste your website URL first');
      return;
    }
    setBusy(true);
    const target = `/vendor-onboarding?prefill_url=${encodeURIComponent(trimmed)}&autoscan=1`;
    if (!user) {
      // Anonymous → signup first, then auto-scrape on landing.
      nav(`/signup?intent=vendor&prefill_url=${encodeURIComponent(trimmed)}&next=${encodeURIComponent(target)}`);
    } else {
      nav(target);
    }
  };

  return (
    <div
      data-testid="vendor-quickstart-card"
      className="mt-4 bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-700 rounded-2xl p-5 sm:p-6 shadow-md text-white relative overflow-hidden"
    >
      <div className="bg-grain absolute inset-0 opacity-20 pointer-events-none" />
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-3 sm:max-w-[280px]">
          <div className="h-11 w-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
            <Globe className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-emerald-100/80 font-medium">For merchants</div>
            <div className="font-semibold leading-tight">Have a website? We'll build your store in 60s.</div>
          </div>
        </div>
        <div className="flex-1 flex flex-col sm:flex-row gap-2">
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') go(); }}
            placeholder="https://yourstore.com"
            disabled={busy}
            className="h-11 bg-white/95 text-slate-900 placeholder:text-slate-400 border-0 focus-visible:ring-2 focus-visible:ring-white"
            data-testid="hero-vendor-url-input"
          />
          <Button
            onClick={go}
            disabled={busy}
            className="h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium px-5 whitespace-nowrap"
            data-testid="hero-vendor-scan-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
            Scan my site
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VendorQuickStart;
